<template>
  <draggable
      :model-value="items"
      item-key="key"
      handle=".drag-handle"
      animation="200"
      @update:model-value="$emit('update:items', $event)"
  >
    <template #item="{ element: item, index }">
      <div
          class="attachment-row d-flex align-center ga-3 pa-2 mb-2 rounded-lg"
          :class="{ 'attachment-row--error': item.status === 'error' || isHighlighted(item) }"
      >
        <v-icon class="drag-handle flex-shrink-0" size="20" :title="t('interview.dragToReorder')">
          mdi-drag-vertical
        </v-icon>
        <div class="attachment-position flex-shrink-0">{{ index + 1 }}</div>

        <div class="attachment-thumb flex-shrink-0 d-flex align-center justify-center rounded">
          <v-img
              v-if="item.status === 'ready' && item.pages.length"
              :src="item.pages[0].url"
              width="64"
              height="48"
          >
            <template v-slot:error>
              <div class="d-flex align-center justify-center fill-height">
                <v-icon :icon="kindIcon(item.kind)" color="grey"></v-icon>
              </div>
            </template>
          </v-img>
          <v-progress-circular
              v-else-if="item.status === 'uploading'"
              indeterminate
              size="22"
              width="2"
              color="primary"
          ></v-progress-circular>
          <v-icon v-else-if="item.status === 'error'" icon="mdi-alert-circle-outline" color="error"></v-icon>
          <v-icon v-else :icon="kindIcon(item.kind)" color="primary"></v-icon>
        </div>

        <div class="flex-grow-1" style="min-width: 0;">
          <div class="text-body-2 font-weight-medium text-truncate" :title="item.filename">
            {{ item.filename }}
          </div>
          <div v-if="item.status === 'ready'" class="text-caption text-medium-emphasis">
            {{ kindLabel(item.kind) }} · {{ t('attachmentpreview.pageCount', { count: item.page_count }, item.page_count) }}
          </div>
          <template v-else-if="item.status === 'uploading'">
            <div class="text-caption text-medium-emphasis">
              {{ item.progress < 100
                  ? t('interview.uploadingStimulusMaterial')
                  : t('interview.processingStimulusMaterial') }}
            </div>
            <v-progress-linear
                :model-value="item.progress"
                :indeterminate="item.progress >= 100"
                color="primary"
                height="4"
                rounded
                class="mt-1"
            ></v-progress-linear>
          </template>
          <div v-else class="text-caption text-error">{{ item.error }}</div>
          <div v-if="isHighlighted(item)" class="text-caption text-error">{{ highlightDetail }}</div>
        </div>

        <div class="d-flex align-center flex-shrink-0">
          <v-btn
              v-if="item.status === 'error' && item.retryable"
              variant="text"
              size="small"
              color="primary"
              prepend-icon="mdi-refresh"
              @click="$emit('retry', item.key)"
          >
            {{ t('interview.retryStimulusMaterial') }}
          </v-btn>
          <v-btn
              icon="mdi-arrow-up"
              variant="text"
              size="small"
              density="comfortable"
              :disabled="index === 0"
              :title="t('interview.moveStimulusMaterialUp')"
              :aria-label="t('interview.moveStimulusMaterialUp')"
              @click="move(index, index - 1)"
          ></v-btn>
          <v-btn
              icon="mdi-arrow-down"
              variant="text"
              size="small"
              density="comfortable"
              :disabled="index === items.length - 1"
              :title="t('interview.moveStimulusMaterialDown')"
              :aria-label="t('interview.moveStimulusMaterialDown')"
              @click="move(index, index + 1)"
          ></v-btn>
          <v-btn
              icon="mdi-close"
              variant="text"
              size="small"
              density="comfortable"
              color="error"
              :title="t('interview.removeStimulusMaterial')"
              :aria-label="t('interview.removeStimulusMaterial')"
              @click="$emit('remove', item.key)"
          ></v-btn>
        </div>
      </div>
    </template>
  </draggable>
</template>

<script>
import draggable from 'vuedraggable';
import { useI18n } from "vue-i18n";
import { ATTACHMENT_KINDS, moveAttachment } from "@/utils/templateAttachment";

// The start form's stimulus materials in the order the group will see them, one
// row per file with its upload state. A reorder (drag or up/down) goes out as a
// new array through `update:items`; removing and retrying are left to the
// parent, which owns the uploads.
export default {
  name: 'AttachmentUploadList',
  components: { draggable },
  props: {
    items: {
      type: Array,
      required: true,
    },
    // file_ids the last start error named, shown with its detail.
    highlightedFileIds: {
      type: Array,
      default: () => [],
    },
    highlightDetail: {
      type: String,
      default: '',
    },
  },
  emits: ['update:items', 'remove', 'retry'],
  setup() {
    const { t } = useI18n();

    return {
      t
    };
  },
  methods: {
    move(from, to) {
      this.$emit('update:items', moveAttachment(this.items, from, to));
    },
    isHighlighted(item) {
      return !!item.file_id && this.highlightedFileIds.includes(item.file_id);
    },
    kindIcon(kind) {
      return ATTACHMENT_KINDS[kind]?.icon || 'mdi-file-outline';
    },
    kindLabel(kind) {
      const labelKey = ATTACHMENT_KINDS[kind]?.labelKey;
      return labelKey ? this.t(labelKey) : (kind || '');
    },
  },
};
</script>

<style scoped>
.attachment-row {
  border: 1px solid rgba(0, 0, 0, 0.12);
  background-color: #fafafa;
}

.attachment-row--error {
  border-color: rgb(var(--v-theme-error));
  background-color: rgba(var(--v-theme-error), 0.04);
}

.drag-handle {
  cursor: grab;
  color: #bdbdbd;
}

.attachment-position {
  min-width: 20px;
  text-align: center;
  font-weight: 700;
}

.attachment-thumb {
  width: 64px;
  height: 48px;
  overflow: hidden;
  background-color: #f5f5f5;
  border: 1px solid #e0e0e0;
}
</style>
